import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { DashboardService } from '@core/services/http/dashboard.service';
import { PtaxCurveCurrency } from '@interfaces/a-organizar/ptaxCurve';
import { TranslateService } from '@ngx-translate/core';
import { VaultTranslatedBaseComponent } from '@shared/components/base/vault-translated-base.component';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-ptax-curve-chart',
  imports: [CommonModule, VaultDateTimePipe],
  template: `
    <div class="ptax-curve-chart">
      <div class="ptax-curve-chart__header">
        <div class="ptax-curve-chart__heading">
          <div class="card-info__item__title card-info__item__title--highlight">
            Curva PTAX · Últimos 60 dias
          </div>
          <div class="ptax-curve-chart__source">
            <span>Fonte: </span>
            <a
              href="https://www.bcb.gov.br/estabilidadefinanceira/historicocotacoes"
              target="_blank"
              rel="noopener noreferrer"
              class="ptax-curve-chart__source-link">
              Banco Central do Brasil (PTAX)
              <i class="pi pi-external-link"></i>
            </a>
          </div>
        </div>
        <div
          class="ptax-curve-chart__last-update"
          *ngIf="ptaxData.length > 0 && ptaxData[0]?.lastUpdate">
          <span>Atualizado {{ ptaxData[0].lastUpdate | vaultDateTime }}</span>
        </div>
      </div>
      <div class="ptax-curve-chart__plot">
        <img
          class="ptax-curve-chart__watermark"
          src="assets/images/logo-cognito.svg"
          alt=""
          aria-hidden="true" />
        <div class="ptax-curve-chart__container" #ptaxCurveChart></div>
      </div>
    </div>
  `,
  styleUrls: ['./ptax-curve-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PtaxCurveChartComponent
  extends VaultTranslatedBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('ptaxCurveChart', { static: true }) ptaxCurveChartElement!: ElementRef;

  private destroy$ = new Subject<void>();
  private chart: any | undefined;
  ptaxData: PtaxCurveCurrency[] = [];
  isLoading = false;
  private isBrowser: boolean;

  constructor(
    private dashboardService: DashboardService,
    private dateFormatter: DateFormatterService,
    private cdr: ChangeDetectorRef,
    translateService: TranslateService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(translateService);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.loadPtaxCurve();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyChart();
  }

  private loadPtaxCurve(): void {
    this.isLoading = true;
    this.dashboardService
      .getPtaxCurveLast60Days()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const ptaxData: PtaxCurveCurrency[] = Array.isArray(response) ? response : [];
          this.ptaxData = ptaxData;
          this.updateChart();
          this.cdr.markForCheck();
        },
        error: () => {
          this.ptaxData = [];
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Chave estável de dia de cotação (`YYYY-MM-DD`), ignorando qualquer horário/fuso vindo da API.
   */
  private toDateKey(dateInput: string | Date): string {
    return this.dateFormatter.formatDateToYYYYMMDD(dateInput);
  }

  private async updateChart(): Promise<void> {
    if (!this.ptaxData.length || !this.ptaxCurveChartElement || !this.isBrowser) {
      return;
    }

    try {
      const ApexCharts = (await import('apexcharts')).default;

      // Eixo X: datas únicas ordenadas.
      // A API devolve `quoteDate` como meia-noite UTC ("2026-08-04T00:00:00Z"). A PTAX é uma
      // data de calendário (dia da cotação), não um instante — por isso o parse trunca a hora
      // e interpreta a data no fuso local. Sem isso, todo rótulo cai para D-1 em fusos
      // negativos (ex.: America/Sao_Paulo).
      const allDates = Array.from(
        new Set(
          this.ptaxData.flatMap(currency =>
            currency.points.map(point => this.toDateKey(point.quoteDate))
          )
        )
      )
        .map(dateKey => this.dateFormatter.parseDateTruncateTime(dateKey) as Date)
        .sort((a, b) => a.getTime() - b.getTime());

      // Verifica se todas as datas são do mesmo ano
      const years = [...new Set(allDates.map(date => date.getFullYear()))];
      const isSameYear = years.length === 1;

      // Formata as datas baseado na verificação
      const formattedDates = allDates.map(date => {
        if (isSameYear) {
          return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        } else {
          return date.toLocaleDateString('pt-BR');
        }
      });

      // Monta as séries alinhadas com allDates (apenas y), evitando mismatch de formato com categories
      const series = this.ptaxData.map(currency => {
        const pointsByDate = new Map(
          currency.points.map(point => [this.toDateKey(point.quoteDate), point.saleRate])
        );
        return {
          name: currency.currencyCode,
          data: allDates.map(date => pointsByDate.get(this.toDateKey(date)) ?? null),
        };
      });

      // Cores fixas por moeda (não depende da ordem das séries). USD e EUR são as
      // únicas exibidas por padrão, então recebem matizes bem distintas (azul x magenta)
      // para não parecerem a mesma cor; as demais completam uma paleta categórica
      // separável (verde/dourado/vermelho), sem matizes vizinhas entre si.
      const currencyColors: Record<string, string> = {
        USD: '#2563EB', // azul
        EUR: '#A21CAF', // magenta/roxo
        CHF: '#16A34A', // verde
        JPY: '#CA8A04', // dourado
        CNY: '#DC2626', // vermelho
      };
      const fallbackColors = ['#0EA5E9', '#DB2777', '#9333EA', '#65A30D', '#475569'];
      const colors = series.map(
        (serie, index) =>
          currencyColors[serie.name] ?? fallbackColors[index % fallbackColors.length]
      );

      const options: any = {
        series: series,
        colors: colors,
        chart: {
          type: 'line',
          height: 400,
          width: '100%',
          redrawOnWindowResize: true,
          animations: {
            enabled: true,
            speed: 800,
          },
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 6,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
        },
        stroke: {
          width: 2,
          curve: 'smooth',
        },
        xaxis: {
          categories: formattedDates,
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: "'Inter', sans-serif",
              colors: '#6C757D',
            },
            rotate: -45,
            rotateAlways: true,
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: "'Inter', sans-serif",
              colors: '#6C757D',
            },
            formatter: (value: number) =>
              value ? value.toLocaleString('pt-BR', { minimumFractionDigits: 4 }) : '',
          },
          title: {
            text: 'Cotação de venda (PTAX)',
            style: {
              fontSize: '14px',
              fontFamily: "'Inter', sans-serif",
              color: '#6C757D',
            },
          },
        },
        grid: {
          borderColor: '#E5E5E5',
          strokeDashArray: 3,
          padding: {
            top: 24,
            right: 10,
            bottom: 0,
            left: 8,
          },
        },
        markers: {
          size: 3,
          strokeColors: '#fff',
          strokeWidth: 1,
          hover: {
            size: 6,
          },
        },
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          theme: 'light',
          style: {
            fontSize: '12px',
            fontFamily: "'Inter', sans-serif",
          },
          custom: ({
            series,
            seriesIndex,
            dataPointIndex,
            w,
          }: {
            series: number[][];
            seriesIndex: number;
            dataPointIndex: number;
            w: any;
          }) => {
            // Reconstroi a data completa para formatação
            const originalDate = allDates[dataPointIndex];
            const formattedDate = originalDate.toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });

            // Constrói HTML para todas as moedas visíveis
            let currenciesHtml = '';

            // Itera por todas as séries para mostrar valores de moedas visíveis
            for (let i = 0; i < series.length; i++) {
              const value = series[i][dataPointIndex];
              const currencyCode = w.globals.seriesNames[i];
              const isVisible = !w.globals.collapsedSeries.includes(currencyCode);

              if (isVisible && value !== null && value !== undefined) {
                const formattedValue = value.toLocaleString('pt-BR', {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                });

                currenciesHtml += `
                  <div style="margin-bottom: 4px;">
                    <span style="font-weight: 600; color: #1c1c1c;">
                      PTAX ${currencyCode}: ${formattedValue}
                    </span>
                  </div>
                `;
              }
            }

            return `
              <div style="padding: 8px 12px; background: #fff; border: 1px solid #e5e5e5; border-radius: 6px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);">
                ${currenciesHtml}
                <div style="font-size: 11px; color: #6c757d; margin-bottom: 2px; margin-top: 4px; border-top: 1px solid #f0f0f0; padding-top: 4px;">
                  Data: ${formattedDate}
                </div>
                <div style="font-size: 11px; color: #6c757d;">
                  Cotação de venda
                </div>
              </div>
            `;
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'center',
          offsetX: -28,
          offsetY: 6,
          fontSize: '13px',
          fontFamily: "'Inter', sans-serif",
          labels: {
            colors: '#6C757D',
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300,
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      };

      this.destroyChart();
      this.chart = new ApexCharts(this.ptaxCurveChartElement.nativeElement, options);
      this.chart.render().then(() => {
        // Esconde as moedas que não são EUR ou USD
        const toHide = this.ptaxData
          .map(c => c.currencyCode)
          .filter(code => code !== 'EUR' && code !== 'USD');
        toHide.forEach(code => {
          this.chart?.toggleSeries(code);
        });
      });
    } catch (error) {
      console.error('Erro ao carregar ApexCharts:', error);
    }
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }
}
